import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { GlobalVariables } from 'app/services/globals';
import { generateUniqueId } from 'app/shared/utils/naming.util';

import { AssertExpressionType, Collection, IViolation, Owned, stringIsNullOrEmpty } from 'shell-models-lib';
import { BooleanDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { AssertExpression } from './assert-expression';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { asyncUpdateIdentifiers, updateIdentifiers } from './dataflow-expression-update-identifiers';
import { convertCacheSinksToArguments, convertColumnRefToDataFlowColumn, convertParametersToArguments, IDataFlowArguments } from './dataflow-expression-utilities';
import { DataFlowExpression } from './dataflowexpression';
import { Transformation } from './transformation';

@Metamodel.model({
    name: 'assert',
    display: [stringResources.DataFlowAssertTransformationLabel, stringResources.DataFlowAssertTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    }
})
export class AssertTransformation extends Transformation {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        type: AssertExpression.MODEL,
        collection: Collection.Array,
        constraint: {
            validator: AssertTransformation.validateAssertExpressions
        }
    })
    public assertExpressions: AssertExpression[] = [];

    public abort: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    public constructor() {
        super();
    }

    private static validateAssertExpressions(transformation: AssertTransformation): IViolation[] {
        const violations: IViolation[] = [];
        let foundBadExpression = false;
        transformation.assertExpressions.forEach((assertExpression) => {
            switch (assertExpression.assertType) {
                case AssertExpressionType.ExpectTrue:
                    if (stringIsNullOrEmpty(assertExpression.trueExpression.expression)) {
                        foundBadExpression = true;
                    }
                break;
                case AssertExpressionType.ExpectUnique:
                    if (assertExpression.uniqueExpressions.find((uniqueExp) => stringIsNullOrEmpty(uniqueExp.expression))) {
                        foundBadExpression = true;
                    }
                break;
                case AssertExpressionType.ExpectExists:
                    if (stringIsNullOrEmpty(assertExpression.existsLeftExpression.expression)) {
                        foundBadExpression = true;
                    }
                    if (stringIsNullOrEmpty(assertExpression.existsRightExpression.expression)) {
                        foundBadExpression = true;
                    }
                break;
                default:
                break;
            }
        });
        if (foundBadExpression) {
            violations.push({
                context: transformation,
                message: () => stringResources.AssertNoExpression
            });
        }
        if (transformation.assertExpressions.find((assert) => stringIsNullOrEmpty(assert.assertId))) {
            violations.push({
                context: transformation,
                message: () => stringResources.AssertNoId
            });
        }
        return violations;
    }

    public getExpressions(): DataFlowExpression[] {
        const expressions: DataFlowExpression[] = [];
        this.assertExpressions.forEach((assertExpression) => {
            expressions.push(assertExpression.trueExpression);
            assertExpression.uniqueExpressions.forEach((uniqueExpression) => {
                expressions.push(uniqueExpression);
            });
            expressions.push(assertExpression.existsLeftExpression);
            expressions.push(assertExpression.existsRightExpression);
            expressions.push(assertExpression.filterExpression);
        });
        return expressions;
    }

    public addAssertExpression(): AssertExpression {
        const assertExpression = Owned.create<AssertExpression>(AssertExpression.MODEL, this);
        assertExpression.guid = generateUniqueId();
        assertExpression.assertId = `assert${this.assertExpressions.length + 1}`;
        this.assertExpressions.push(assertExpression);
        return assertExpression;
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
        const availableArguments: IDataFlowArguments = {
            dataFlowColumns: convertColumnRefToDataFlowColumn(this.distinctInputColumnRefs),
            dataFlowFunctions: DataFlowExpressionFunctions.ALL_FUNCTIONS,
            dataFlowParameters: convertParametersToArguments(this.dataflow.parameters),
            dataFlowTransientColumns: convertColumnRefToDataFlowColumn(this.transientColumnRefs),
            dataFlowCacheSinks: convertCacheSinksToArguments(this.dataflow.cacheSinks)
        };
        this.assertExpressions.forEach((assertExpression) => {
            if (GlobalVariables.validateDataFlowAPI) {
                asyncUpdateIdentifiers(availableArguments, assertExpression.trueExpression, this);
                assertExpression.uniqueExpressions.forEach((uniqueExpression) => {
                    asyncUpdateIdentifiers(availableArguments, uniqueExpression, this);
                });
                asyncUpdateIdentifiers(availableArguments, assertExpression.existsLeftExpression, this);
                asyncUpdateIdentifiers(availableArguments, assertExpression.existsRightExpression, this);
                asyncUpdateIdentifiers(availableArguments, assertExpression.filterExpression, this);
            } else {
                updateIdentifiers(availableArguments, assertExpression.trueExpression);
                assertExpression.uniqueExpressions.forEach((uniqueExpression) => {
                    updateIdentifiers(availableArguments, uniqueExpression);
                });
                updateIdentifiers(availableArguments, assertExpression.existsLeftExpression);
                updateIdentifiers(availableArguments, assertExpression.existsRightExpression);
                updateIdentifiers(availableArguments, assertExpression.filterExpression);
            }
        });
    }

    protected getGeneratedDescription(): string {
        return stringResources.AssertTransformationDescription;
    }
}
